import { Mic, MicOff, Video, VideoOff, PhoneOff, Monitor, Users, MessageSquare, Activity, Maximize, Minimize, Hand, RefreshCw, Circle, Square } from 'lucide-react';
import Button from '../ui/Button';
import { useRoom } from '../../contexts/RoomContext';
import { useMediaStore } from '../../stores/mediaStore';
import { useParticipantsStore } from '../../stores/participantsStore';
import { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';

interface ControlBarProps {
  onToggleSidebar?: () => void;
  onToggleChat?: () => void;
  onToggleMonitor?: () => void;
  showMonitorButton?: boolean;
  unreadMessages?: number;
}

const ControlBar = ({ onToggleSidebar, onToggleChat, onToggleMonitor, showMonitorButton = false, unreadMessages = 0 }: ControlBarProps) => {
  const { toggleAudio, toggleVideo, startScreenShare, stopScreenShare, leaveRoom, toggleRaisedHand, resyncProducers, recordingState, startLocalRecording, stopLocalRecording } = useRoom();
  const { audioEnabled, videoEnabled, screenShareEnabled } = useMediaStore();
  const { localParticipant } = useParticipantsStore();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [showRecordingMenu, setShowRecordingMenu] = useState(false);
  const [recordingMode, setRecordingMode] = useState<'video-audio' | 'audio-only'>('video-audio');
  const [recordingQuality, setRecordingQuality] = useState<'high' | 'medium' | 'low'>('medium');
  const [recordingNow, setRecordingNow] = useState(Date.now());
  const recordingMenuRef = useRef<HTMLDivElement | null>(null);
  
  const raisedHand = localParticipant?.raisedHand || false;
  const recordingDurationLabel = useMemo(() => {
    if (!recordingState.isRecording || !recordingState.startedAt) {
      return '00:00';
    }

    const elapsedSeconds = Math.max(0, Math.floor((recordingNow - recordingState.startedAt) / 1000));
    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const seconds = elapsedSeconds % 60;

    if (hours > 0) {
      return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
    }

    return [minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  }, [recordingNow, recordingState.isRecording, recordingState.startedAt]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const updateDesktopState = () => {
      setIsDesktop(window.innerWidth >= 640);
    };

    updateDesktopState();
    window.addEventListener('resize', updateDesktopState);
    return () => window.removeEventListener('resize', updateDesktopState);
  }, []);

  useEffect(() => {
    if (recordingState.isRecording) {
      setShowRecordingMenu(false);
    }
  }, [recordingState.isRecording]);

  useEffect(() => {
    if (!showRecordingMenu) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!recordingMenuRef.current) {
        return;
      }

      if (!recordingMenuRef.current.contains(event.target as Node)) {
        setShowRecordingMenu(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showRecordingMenu]);

  useEffect(() => {
    if (!recordingState.isRecording) {
      return;
    }

    setRecordingNow(Date.now());
    const timerId = window.setInterval(() => {
      setRecordingNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [recordingState.isRecording, recordingState.startedAt]);

  const handleScreenShare = () => {
    if (screenShareEnabled) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  };

  const handleStartRecording = async () => {
    try {
      await startLocalRecording(recordingMode, recordingQuality);
    } catch (error: any) {
      toast.error(error?.message || 'Не удалось начать запись');
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div className="bg-gray-800 border-t border-gray-700 px-2 sm:px-6 py-2 sm:py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-1 sm:gap-0">
        {/* Left controls */}
        <div className="flex items-center gap-1 sm:gap-3">
          <Button
            variant={audioEnabled ? 'secondary' : 'danger'}
            size="lg"
            onClick={toggleAudio}
            className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center"
            title={audioEnabled ? 'Выключить микрофон' : 'Включить микрофон'}
          >
            {audioEnabled ? (
              <Mic className="w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </Button>

          <Button
            variant={videoEnabled ? 'secondary' : 'danger'}
            size="lg"
            onClick={toggleVideo}
            className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center"
            title={videoEnabled ? 'Выключить камеру' : 'Включить камеру'}
          >
            {videoEnabled ? (
              <Video className="w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </Button>

          <Button
            variant={screenShareEnabled ? 'primary' : 'secondary'}
            size="lg"
            onClick={handleScreenShare}
            className="hidden sm:flex rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 items-center justify-center"
            title={screenShareEnabled ? 'Остановить демонстрацию' : 'Демонстрация экрана'}
          >
            <Monitor className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>

          <Button
            variant={raisedHand ? 'primary' : 'secondary'}
            size="lg"
            onClick={toggleRaisedHand}
            className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center"
            title={raisedHand ? 'Опустить руку' : 'Поднять руку'}
          >
            <Hand className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
        </div>

        {/* Center - Leave button */}
        <div>
          <Button
            variant="danger"
            size="lg"
            onClick={leaveRoom}
            className="rounded-full px-3 sm:px-8 h-10 sm:h-auto"
          >
            <PhoneOff className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
            <span className="hidden sm:inline">Завершить звонок</span>
          </Button>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1 sm:gap-3 relative">
          {isDesktop && (
            <div ref={recordingMenuRef} className="relative hidden sm:block">
              {recordingState.isRecording ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={stopLocalRecording}
                    className="rounded-full px-3 h-10 flex items-center gap-2"
                    title={`Остановить запись (${recordingDurationLabel})`}
                  >
                    <Square className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                    <span className="text-xs sm:text-sm font-medium tabular-nums">{recordingDurationLabel}</span>
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => setShowRecordingMenu((prev) => !prev)}
                    className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center"
                    title="Запись"
                  >
                    <Circle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                  </Button>

                  {showRecordingMenu && (
                    <div className="absolute bottom-full right-0 mb-3 w-80 rounded-2xl border border-gray-700 bg-gray-800 p-4 shadow-2xl z-30">
                      <div className="space-y-4">
                        <div>
                          <div className="text-sm font-semibold text-white">Запись</div>
                          <div className="text-xs text-gray-400 mt-1">Экран конференции и звук участников сохраняются локально на ваш компьютер.</div>
                          {recordingMode === 'video-audio' && (
                            <div className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs text-amber-100 shadow-[0_0_0_1px_rgba(245,158,11,0.15)]">
                              В окне браузера при выборе экрана для записи убедитесь, что включена опция <br /><span className="font-semibold">Предоставить доступ к аудио на вкладке</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="text-xs font-medium text-gray-300 mb-2">Режим</div>
                          <div className="grid grid-cols-1 gap-2">
                            <button
                              type="button"
                              onClick={() => setRecordingMode('video-audio')}
                              className={`rounded-xl border px-3 py-2 text-left transition-all ${recordingMode === 'video-audio' ? 'border-blue-400 bg-blue-500/20 text-white ring-2 ring-blue-400/40 shadow-[0_0_0_1px_rgba(96,165,250,0.25)]' : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-500 hover:bg-gray-850'}`}
                            >
                              Видео + аудио
                            </button>
                            <button
                              type="button"
                              onClick={() => setRecordingMode('audio-only')}
                              className={`rounded-xl border px-3 py-2 text-left transition-all ${recordingMode === 'audio-only' ? 'border-blue-400 bg-blue-500/20 text-white ring-2 ring-blue-400/40 shadow-[0_0_0_1px_rgba(96,165,250,0.25)]' : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-500 hover:bg-gray-850'}`}
                            >
                              Только аудио
                            </button>
                          </div>
                        </div>

                        {recordingMode === 'video-audio' && (
                          <div>
                            <div className="text-xs font-medium text-gray-300 mb-2">Качество записи</div>
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                type="button"
                                onClick={() => setRecordingQuality('high')}
                                className={`rounded-xl border px-3 py-2 text-sm transition-colors ${recordingQuality === 'high' ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-600'}`}
                              >
                                Высокое
                              </button>
                              <button
                                type="button"
                                onClick={() => setRecordingQuality('medium')}
                                className={`rounded-xl border px-3 py-2 text-sm transition-colors ${recordingQuality === 'medium' ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-600'}`}
                              >
                                Среднее
                              </button>
                              <button
                                type="button"
                                onClick={() => setRecordingQuality('low')}
                                className={`rounded-xl border px-3 py-2 text-sm transition-colors ${recordingQuality === 'low' ? 'border-blue-500 bg-blue-500/10 text-white' : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-600'}`}
                              >
                                Низкое
                              </button>
                            </div>
                          </div>
                        )}

                        <Button
                          variant="primary"
                          size="md"
                          onClick={handleStartRecording}
                          className="w-full"
                        >
                          Начать запись
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <Button
            variant="secondary"
            size="lg"
            onClick={resyncProducers}
            className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center"
            title="Синхронизация"
          >
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>

          <Button
            variant="secondary"
            size="lg"
            onClick={toggleFullscreen}
            className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center"
            title={isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}
          >
            {isFullscreen ? (
              <Minimize className="w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <Maximize className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </Button>

          {showMonitorButton && (
            <Button
              variant="secondary"
              size="lg"
              onClick={onToggleMonitor}
              className="hidden sm:flex rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 items-center justify-center"
              title="WebRTC Monitor"
            >
              <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          )}

          <Button
            variant="secondary"
            size="lg"
            onClick={onToggleSidebar}
            className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center"
            title="Участники"
          >
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>

          <Button
            variant="secondary"
            size="lg"
            onClick={onToggleChat}
            className="rounded-full w-10 h-10 sm:w-12 sm:h-12 !p-0 flex items-center justify-center relative"
            title="Чат"
          >
            <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
            {unreadMessages > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 rounded-full w-3 h-3" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ControlBar;
